// The default table mixes this component into resourceTable(), while custom
// endless layouts commonly nest their own resourceInfinit() below that table.
// A WeakMap lets both Alpine scopes share one monotonic generation without
// adding transport fields or observable attributes to legacy table markup.
const endlessGenerations = new WeakMap();

/**
 * Endless (infinite) scrolling for a resource table — the core engine used both by the default
 * table (forward-only) and by custom card layouts (bidirectional + pull-to-refresh, e.g. a
 * calendar-style list).
 *
 * Fetches only the items HTML + next/prev cursors via the renderless Livewire method
 * `fetchCursorFragment(direction, cursor)` and appends/prepends them to the
 * list. Works for the row layout and the mobile card layout alike.
 *
 * Append target resolution (in order): an explicit `[x-ref="list"]` inside the component (custom
 * layouts), else a `[data-endless-items]` container, else the default `table tbody` — searched
 * within the enclosing `.resourcerer-table-content`.
 *
 * @param {{prev?: string|null, next?: string|null, scrollSelector?: string|null, queryFingerprint?: string|null}} opts
 */
export default (opts = {}) => ({
  prev: opts.prev ?? null,
  next: opts.next ?? null,
  loading: false,
  // Overridable scroll container (app layouts differ). No package default — falls back to the
  // document scrolling element; apps set config `resourcerer.scroll_selector` for a custom one.
  scrollSelector: opts.scrollSelector ?? null,
  queryFingerprint: opts.queryFingerprint ?? null,
  _localEndlessGeneration: 0,

  // Nested custom layouts get their seeds from x-data. resourceTable overrides
  // init(), so the stock keyed descendant calls seedEndless() after its own seeds.
  init() {
    this._restoreEndlessState();
  },

  seedEndless({ prev = null, next = null, queryFingerprint = null, scrollSelector = null } = {}) {
    this.prev = prev;
    this.next = next;
    this.queryFingerprint = queryFingerprint;
    this.scrollSelector = scrollSelector;
    this._restoreEndlessState();
  },

  _restoreEndlessState() {
    const list = this._listEl();
    if (!list) return;

    // Livewire's navigation cache serializes HTML, not Alpine's advanced state.
    // Keep the boundaries on the exact accumulated list, not in a query-keyed
    // global cache: a server morph removes this attribute with the old rows.
    try {
      const saved = JSON.parse(list.getAttribute('data-endless-state'));
      const cursor = value => value === null || typeof value === 'string';
      if (saved && saved.queryFingerprint === this.queryFingerprint
        && cursor(saved.prev) && cursor(saved.next)) {
        this.prev = saved.prev;
        this.next = saved.next;
      }
    } catch { /* Invalid/legacy state leaves the server seeds intact. */ }
  },

  _rememberEndlessState(list) {
    list.setAttribute('data-endless-state', JSON.stringify({
      queryFingerprint: this.queryFingerprint, prev: this.prev, next: this.next,
    }));
  },

  pulling: false,
  pullStartY: null,
  pullDistance: 0,
  loadingPrev: false,

  // After a wire:navigate teardown, Alpine magics on stale elements lose their
  // component ($root/$el undefined) while queued triggers (x-intersect, timers)
  // may still fire — treat that as "gone" and no-op instead of throwing.
  _isTornDown() {
    return !this.$root || !this.$root.isConnected;
  },

  _listEl() {
    if (this._isTornDown()) {
      return null;
    }

    // Explicit list marker inside the component (custom card layouts), else fall back to a
    // marked container / the default table body within this table.
    const explicit = this.$root.querySelector('[x-ref="list"]');
    if (explicit) {
      return explicit;
    }

    const scope = this.$el.closest('.resourcerer-table-content') || this.$root;

    return scope.querySelector('[data-endless-items]') || scope.querySelector('table tbody');
  },

  _endlessGenerationRoot() {
    if (!this.$root) {
      return null;
    }

    return this.$root.closest?.('.resourcerer-table') || this.$root;
  },

  _currentEndlessGeneration() {
    const root = this._endlessGenerationRoot();

    return root
      ? (endlessGenerations.get(root) ?? 0)
      : this._localEndlessGeneration;
  },

  _invalidateEndlessRequests() {
    const root = this._endlessGenerationRoot();
    if (root) {
      endlessGenerations.set(root, (endlessGenerations.get(root) ?? 0) + 1);
      return;
    }

    this._localEndlessGeneration += 1;
  },

  _retryDiscardedEndlessLoad(direction) {
    queueMicrotask(() => {
      const token = direction === 'prev' ? this.prev : this.next;
      if (this._isTornDown() || this.loading || !token) {
        return;
      }

      // IntersectionObserver callbacks are edge-triggered. When a request is
      // discarded while its sentinel remains visible, resetting `loading`
      // alone does not invoke x-intersect again. Continue the already requested
      // direction once with the current generation and cursor.
      void this.load(direction);
    });
  },

  _removeIncomingDuplicates(fragment, list) {
    // Keep displayed rows authoritative, even when an overlapping fragment has
    // different content. IDs are opaque strings, never selectors or numbers.
    const seen = new Set([...list.querySelectorAll('[data-row-id]')]
      .map(el => el.getAttribute('data-row-id')));

    for (const el of fragment.querySelectorAll('[data-row-id]')) {
      if (!fragment.contains(el)) continue;
      const id = el.getAttribute('data-row-id');
      if (seen.has(id)) {
        // Custom cards commonly put the identity inside an interactive wrapper.
        // Removing only the card would leave its row actions/Alpine scope behind.
        (el.closest('.resourcerer-row') || el).remove();
      } else {
        seen.add(id);
      }
    }
  },

  async load(direction) {
    if (this._isTornDown() || this.loading) {
      return;
    }
    const token = direction === 'prev' ? this.prev : this.next;
    if (!token) {
      return;
    }
    const requestList = this._listEl();
    if (!requestList) {
      return;
    }
    this.loading = true;
    this.loadingPrev = direction === 'prev';
    const requestFingerprint = this.queryFingerprint;
    const requestGeneration = this._currentEndlessGeneration();
    let retryDiscardedLoad = false;

    try {
      const res = await this.$wire.fetchCursorFragment(direction, token);
      // A deterministic fingerprint returns to its old value after A→B→A.
      // The surrounding resourceTable therefore also advances a monotonic
      // generation on query intent and completed morphs. Keep the list-node
      // check as an independent teardown/replacement guard.
      const fingerprintChanged = requestFingerprint !== this.queryFingerprint;
      if (requestGeneration !== this._currentEndlessGeneration()
        || fingerprintChanged
        || !requestList.isConnected
        || requestList !== this._listEl()) {
        // A genuinely different query gets a fresh keyed sentinel/cursor from
        // the table morph. Same-query invalidation (selection, polling, or an
        // A→B→A render) keeps the sentinel in place and therefore needs an
        // explicit continuation after `loading` is reset in finally.
        retryDiscardedLoad = !fingerprintChanged;
        return;
      }

      const tmpl = document.createElement('template');
      tmpl.innerHTML = (res.html || '').trim();
      const fragment = tmpl.content;
      this._removeIncomingDuplicates(fragment, requestList);

      // Append/prepend and only advance the cursor for the direction we moved.
      if (direction === 'prev') {
        // Prepending grows the content above the viewport; measure the scroll container before and
        // after and push scrollTop down by the added height so the visible rows do not jump.
        const scroller = this._scrollContainer();
        const before = scroller ? scroller.scrollHeight : 0;
        requestList.insertBefore(fragment, requestList.firstChild);
        this.prev = res.prev ?? null;
        if (scroller) {
          scroller.scrollTop += scroller.scrollHeight - before;
        }
      } else {
        requestList.appendChild(fragment);
        this.next = res.next ?? null;
      }
      // Only an accepted fragment can serialize boundaries into the current list,
      // including an overlap-only page and explicit null ends.
      this._rememberEndlessState(requestList);
    } finally {
      this.loading = false;
      this.loadingPrev = false;

      if (retryDiscardedLoad) {
        this._retryDiscardedEndlessLoad(direction);
      }
    }
  },

  isMobile() {
    return window.innerWidth < 768; // Tailwind md breakpoint
  },

  // The scrolling element to anchor against. Prefer an app-provided `scrollSelector` (config
  // `resourcerer.scroll_selector`), else fall back to the document's scrolling element.
  _scrollContainer() {
    return (this.scrollSelector && document.querySelector(this.scrollSelector))
      || document.scrollingElement
      || document.documentElement;
  },

  isAtTop() {
    const sc = this._scrollContainer();
    return sc ? sc.scrollTop === 0 : true;
  },

  // Rubber-band style for the list while pulling.
  pullStyle() {
    if (!this.isMobile()) {
      return {};
    }

    let offset = 0;

    if (this.pulling && this.pullDistance > 0) {
      offset = Math.min(this.pullDistance * 0.5, 60);
    } else if (this.loadingPrev) {
      offset = 32;
    } else {
      offset = 0;
    }

    return {
      transform: `translateY(${offset}px)`,
      transition: this.pulling ? 'none' : 'transform 0.15s ease-out',
    };
  },

  handlePullStart(event) {
    if (!this.isMobile()) return;
    if (!this.prev) return; // no earlier items -> nothing to do
    if (!this.isAtTop()) return; // only when scrolled to the very top

    this.pullStartY = event.touches[0].clientY;
    this.pullDistance = 0;
    this.pulling = false;
  },

  handlePullMove(event) {
    if (!this.isMobile()) return;
    if (this.pullStartY === null) return;
    if (!this.isAtTop()) return;

    this.pullDistance = event.touches[0].clientY - this.pullStartY;
    this.pulling = this.pullDistance > 30;
  },

  async handlePullEnd() {
    if (!this.isMobile()) return;

    const shouldLoad = this.pulling && this.pullDistance > 60 && this.prev && !this.loading;

    this.pulling = false;
    this.pullStartY = null;
    this.pullDistance = 0;

    if (shouldLoad) {
      await this.load('prev');
    }
  },
});
